import { useTranslation } from 'react-i18next'
import { Badge } from '../ui/badge'
import { useFormatters } from '../../lib/format'
import type { Intervention } from '../../hooks/useInterventions'

/**
 * WorkOrderChips renders the state of the ordre de service as a short trail of
 * badges: sent → accepted/declined → work reported → validated/disputed.
 *
 * The trail, rather than a single status, is deliberate: a gestionnaire's first
 * question is never "what state is this in" but "did they answer, and when" —
 * so the chips carry their dates. Renders nothing until an OS has actually been
 * sent, so interventions driven entirely in-house stay uncluttered.
 */
export function WorkOrderChips({ intervention }: { intervention: Intervention }) {
    const { t } = useTranslation('workorders')
    const { formatDate } = useFormatters()
    const iv = intervention

    if (!iv.os_sent_at) return null

    const short = (s?: string | null) => (s ? formatDate(s, { dateStyle: 'short' }) : '')

    return (
        <div className="flex flex-wrap items-center gap-2" data-testid="work-order-chips">
            <Badge variant="outline" data-testid="wo-chip-sent">
                {t('chips.os_sent')} · {short(iv.os_sent_at)}
            </Badge>

            {iv.os_response === 'accepted' && (
                <Badge variant="secondary" data-testid="wo-chip-accepted">
                    {t('chips.os_accepted')}
                    {iv.scheduled_at ? ` · ${short(iv.scheduled_at)}` : ''}
                </Badge>
            )}
            {iv.os_response === 'declined' && (
                <Badge variant="destructive" data-testid="wo-chip-declined">
                    {t('chips.os_declined')} · {short(iv.os_responded_at)}
                </Badge>
            )}

            {iv.work_submitted_at && (
                <Badge variant="secondary" data-testid="wo-chip-submitted">
                    {t('chips.work_submitted')} · {short(iv.work_submitted_at)}
                </Badge>
            )}

            {iv.validation_status === 'approved' && (
                <Badge variant="default" data-testid="wo-chip-approved">
                    {t('chips.approved')} · {short(iv.validated_at)}
                </Badge>
            )}
            {iv.validation_status === 'disputed' && (
                <Badge variant="destructive" data-testid="wo-chip-disputed">
                    {t('chips.disputed')}
                </Badge>
            )}
        </div>
    )
}
